import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Math as MathFormula } from "@/components/Math";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function PythagoreanCalculator() {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [c, setC] = useState("");

  const calculateMissing = () => {
    const values = [a, b, c];
    const emptyCount = values.filter(v => !v).length;

    if (emptyCount !== 1) {
      toast({
        title: "Invalid Input",
        description: "Please provide exactly 2 values to calculate the 3rd.",
        variant: "destructive",
      });
      return;
    }

    const aNum = parseFloat(a);
    const bNum = parseFloat(b);
    const cNum = parseFloat(c);

    if (!a) {
      if (cNum <= bNum) {
        toast({
          title: "Invalid Values",
          description: "Hypotenuse must be longer than the other side.",
          variant: "destructive",
        });
        return;
      }
      const result = Math.sqrt(cNum * cNum - bNum * bNum);
      setA(result.toFixed(4));
      toast({
        title: "Calculated",
        description: `Side a = ${result.toFixed(4)}`,
      });
    } else if (!b) {
      if (cNum <= aNum) {
        toast({
          title: "Invalid Values",
          description: "Hypotenuse must be longer than the other side.",
          variant: "destructive",
        });
        return;
      }
      const result = Math.sqrt(cNum * cNum - aNum * aNum);
      setB(result.toFixed(4));
      toast({
        title: "Calculated",
        description: `Side b = ${result.toFixed(4)}`,
      });
    } else if (!c) {
      const result = Math.sqrt(aNum * aNum + bNum * bNum);
      setC(result.toFixed(4));
      toast({
        title: "Calculated",
        description: `Hypotenuse c = ${result.toFixed(4)}`,
      });
    }
  };

  const reset = () => {
    setA("");
    setB("");
    setC("");
  };

  return (
    <PageLayout 
      calculatorName="Pythagorean Calculator" 
      calculatorPath="/calculator/pythagorean"
      category="math"
      subcategory="Geometry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Pythagorean Theorem Calculator</CardTitle>
            <CardDescription>
              Calculate sides of a right triangle
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="a">Side a</Label>
                <Input
                  id="a"
                  type="number"
                  step="any"
                  value={a}
                  onChange={(e) => setA(e.target.value)}
                  placeholder="Leave blank to calculate"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="b">Side b</Label>
                <Input
                  id="b"
                  type="number"
                  step="any"
                  value={b}
                  onChange={(e) => setB(e.target.value)}
                  placeholder="Leave blank to calculate"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="c">Hypotenuse c</Label>
                <Input
                  id="c"
                  type="number"
                  step="any"
                  value={c}
                  onChange={(e) => setC(e.target.value)}
                  placeholder="Leave blank to calculate"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculateMissing} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Pythagorean Theorem</CardTitle>
              </CardHeader>
              <CardContent>
                <MathFormula block>{String.raw`a^2 + b^2 = c^2`}</MathFormula>
                <p className="text-sm text-muted-foreground mt-2 text-center">
                  where c is the hypotenuse (longest side)
                </p>
              </CardContent>
            </Card>
            
            {(a && b && c) && (
              <CalculatorSuperCalcCTA config={calculatorCTAs["pythagorean"]} trigger={parseFloat(a) + parseFloat(b) + parseFloat(c)} />
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
